import * as React from 'react'
import { connect } from 'react-redux'
import type { Dispatch } from 'redux'
import styled, { createGlobalStyle } from 'styled-components'
import HtmlHead from '@components/HtmlHead'
import type { AppStateType } from '../../reducers/appstate'
import { withRouter } from 'next/router'
import {
  CTAPrimary,
  ErrorMessage,
  Form, GlobalStyle,
  H160,
  H250,
  P14,
  P18,
  StyledInput
} from '@components/shared'
import { colors } from '@components/shared/colors'
import { config, Container, Col, Row } from 'react-awesome-styled-grid'
import AuthFormContainer from '@components/auth/AuthForm'
import { redeemCode } from '../../actions/giftActions'
import SimpleNav from '@components/SimpleNav'
import BackgroundGradient from '@components/BackgroundGradient'

type PropsType = {
  userId?: string,
  isSubscribed: boolean,
  errorMessage?: string,
  redeeming: boolean
}

type DispatchPropsType = {
  redeemCode: (code: string | null) => void
}

function Redeem (props: PropsType & DispatchPropsType): React.ReactNode {
  const [code, setCode] = React.useState<string | null>(null)
  const [submitted, setSubmitted] = React.useState<boolean>(false)

  React.useEffect((): void => {
    if (submitted && props.errorMessage) {
      setSubmitted(false)
    }
  }, [props.errorMessage])

  const handleRedeem = (e: any): void => {
    e.preventDefault()
    if (submitted) {
      return
    }
    setSubmitted(true)
    props.redeemCode(code)
  }

  return (
    <Main>
      <HtmlHead
        title={'Dipsea | Redeem Gift'}
        description={'Redeem a year of Dipsea to access 1000+ spicy audiobooks, romance novels, sleep scenes, and steamy sounds.'}
      />
      <GlobalStyle {...({ quadrant: 1 } as any)} />
      <BackgroundGradient quadrant={3} />
      <RedeemBody/>
      <SimpleNav backgroundColor={colors.midnightBlue95}/>
      <Container>
        {!props.isSubscribed && <Row align='center' justify='center'>
          <Col xs={4} sm={6} md={6} lg={8} xl={4} align={'center'}>
            <HeaderContainer>
              <H160 color={colors.sand}
                textAlign={'center'}>{'Redeem your gift code'}</H160>
              <HeaderTextContainer>
                <P18 color={colors.sand}
                  textAlign={'center'}>{props.userId ? 'Next, enter the gift code you received.' : 'First, create your Dipsea account. This is the email and password you\'ll use to log into the Dipsea app.'}</P18>
              </HeaderTextContainer>
            </HeaderContainer>
          </Col>
        </Row>}
        <Row align='center' justify='center'>
          <Col xs={4} sm={6} md={6} lg={8} xl={4} align={'center'}>
            {!props.userId && <StepContainer>
              <P14 color={colors.sand}>STEP 1</P14>
              <AuthFormContainer theme="sand"/>
            </StepContainer>}
            {(props.userId) && <StepContainer>
              <StepTextContainer>
                <P14 color={colors.sand}>STEP 2</P14>
              </StepTextContainer>
              <RedeemHeaderContainer>
                <H250 color={colors.sand}>Enter your gift code</H250>
              </RedeemHeaderContainer>
              <Form onSubmit={handleRedeem}>
                {props.errorMessage && (
                  <ErrorMessage styles={'text-align: center; margin-bottom: 20px;'}>
                    {props.errorMessage}
                  </ErrorMessage>
                )}
                <StyledInput
                  placeholder="Enter Your Gift Code"
                  valid={!props.errorMessage}
                  borderColor={colors.sand}
                  color={colors.sand}
                  onChange={(e: any): void => {
                    setCode(e.target.value)
                  }}/>
                <CtaContainer>
                  <CTAPrimary styles="margin-top: 32px;" width={'100%'}
                    onClick={handleRedeem}>redeem</CTAPrimary>
                </CtaContainer>
              </Form>
            </StepContainer>}
          </Col>
        </Row>
      </Container>
    </Main>
  )
}

const mapStateToProps = (state: AppStateType): { userId: any, isSubscribed: any, redeeming: any, errorMessage: any } => {
  return {
    userId: state.user?.id,
    isSubscribed: state.user?.isSubscribed || false,
    redeeming: state.redeemingGift,
    errorMessage: state.redeemingGiftErrorMessage
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchPropsType => {
  return {
    redeemCode: (code: string | null): void => dispatch(redeemCode(code))
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Redeem) as any) as any

const Main = styled.main<any>`
  position: relative;
`
const HeaderContainer = styled.div<any>`
  margin-top: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  z-index: 1;
`

const HeaderTextContainer = styled.div<any>`
  margin-top: 15px;
`

const StepContainer = styled.div<any>`
  display: flex;
  padding: 32px 22px 32px 22px;
  flex-direction: column;
  border-radius: 10px;
  border: 1px solid ${colors.sand};
  margin-top: 60px;
  margin-bottom: 60px;
  align-items:center;
  ${(props: any): any => config(props).media.sm`
    width: 100%;
    max-width: 465px;
  `}
  ${(props: any): any => config(props).media.lg`
    max-width: 525px;
    align-items: flex-start;
  `}
  z-index: 1;
`

const RedeemHeaderContainer = styled.div<any>`
  margin-top: 6px;
  margin-bottom: 20px;
  width: 100%;
  display: flex;
  justify-content: center;

  ${(props: any): any => config(props).media.lg`
    margin-top: 12px;
    margin-bottom: 22px;
    justify-content: flex-start;
  `}

`

const StepTextContainer = styled.div<any>`
  width: 100%;
  text-align: center;
  color: ${colors.sand};
  ${(props: any): any => config(props).media.lg`
    text-align: left;
  `}
`

const CtaContainer = styled.div<any>`
  margin-top: 32px;
  text-align: center;
  width: 100%;
  ${(props: any): any => config(props).media.lg`
    text-align: left;
  `}
`

const RedeemBody = createGlobalStyle`
  body {
    background-color: ${colors.midnightBlue};
  }
`
